import { min, max } from 'd3-array';

import Indicator from '../Indicator';
import LineMarker from '../markers/LineMarker';

/** Bollinger Bands\u00AE indicator */
export default class BollingerBandsIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 * @property {object[]} componentSeries - Indicator component series
	 */
	_getDefaultParams() {
		return {
			id: 'bollinger',
			name: 'Bollinger Bands®',
			description:
				'Bollinger Bands® are created by plotting the standard deviation around a moving average and are expected to capture approximately 95% of all price action. They are also used to highlight periods of high and low volatility.',
			inputs: [
				{ name: 'period', value: 20 },
				{ name: 'standardDeviations', value: 2, max: 10 }
			],
			componentSeries: [
				{ id: 'top' },
				{ id: 'sma', styleRef: 'sma' },
				{ id: 'bottom' }
			]
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		return min(data.bollinger, i => +i.bottom);
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		return max(data.bollinger, i => +i.top);
	}

	/**
	 * @returns {object}
	 * @property {LineMarker} lineSMA
	 * @property {LineMarker} lineTop
	 * @property {LineMarker} lineBottom
	 * @property {LineMarker} lineFill
	 */
	_getMarkers() {
		return (
			this.markers || {
				lineSMA: new LineMarker(),
				lineTop: new LineMarker(),
				lineBottom: new LineMarker(),
				lineFill: new LineMarker()
			}
		);
	}

	/** @param {object} data - Symbol data object */
	render(data, sessionType) {
		const dataMapTop = this._getDataMap(data.bollinger, 'top');
		const dataMapBottom = this._getDataMap(data.bollinger, 'bottom');
		this.markers.lineFill.renderFillBetween(
			this.panel.rootContext,
			dataMapTop,
			dataMapBottom,
			this.xScale,
			this.yScale,
			this.params.style.background.color
		);
		this._renderLineMarker({
			dataMap: dataMapTop,
			marker: this.markers.lineTop,
			sessionType
		});
		this._renderLineMarker({
			dataMap: dataMapBottom,
			marker: this.markers.lineBottom,
			sessionType
		});
		this._renderLineMarker({
			data: data.bollinger,
			dataKey: 'sma',
			marker: this.markers.lineSMA,
			sessionType
		});
	}
}
